/**
 * 审计日志的事件类型。**单点定义在这里,三端引用**,与契约 AuditEventType 逐字一致。
 *
 * 前三个是决策访谈定下来的(AS-006):三条等式的后半句都是「落了日志」。
 *   contact_unlocked                 抢单后解锁客户联系人(M-08)
 *   priority_window_exclusive        自营优先窗口独占了一单(L0-05)
 *   payment_callback_idempotent_hit  支付回调重复推送被幂等挡下(M-15)
 *
 * 后三个是 S-03 加的,都属于同一件事:**手机号接口出问题时的哨兵**(M-17)。
 *
 * 为什么必须是三个码而不是一个「手机号接口故障」:
 *   phone_quota_exhausted        额度用完了 → 去微信后台充值(该找钱)
 *   phone_service_unavailable    接口挂了或网络不通 → 去查服务(该找人修)
 *   phone_service_unknown_error  微信回了一个我们没见过的错误码 → 谁都不知道该干嘛,
 *                                所以必须单独记下来那个码,而不是归进上面某一类
 * 合成一个码的代价很具体:凌晨三点全站登录失败,值班的人分不清该充值还是该重启。
 *
 * 额度耗尽的表现是**全站登录静默失败**(用户点了没反应,没有 5xx、没有堆栈)。
 * 这几条日志是唯一能让它被看见的东西 —— 所以它们不是「顺手记一笔」,是哨兵。
 *
 * ── S-05 又加了两个,同样是哨兵,但对象是钱 ─────────────────
 *   payment_callback_rejected       验签过了、内容对不上的回调:订单不存在 /
 *                                   金额与下单金额不符 / 交易状态不是成功。
 *                                   处置动作是**人立刻去查这笔钱**(可能是伪造的回调,
 *                                   也可能是我方下单金额与微信侧漂了)。
 *                                   detail 里带 reason 与回调原文
 *   payment_callback_unknown_event  微信推来一个我们没见过的 event_type。
 *                                   处置动作完全不同:去读微信文档,决定这类事件要不要处理
 *
 * 这两个不许合成一个,也不许并进上面那个 idempotent_hit ——
 * 三者的下一步动作分别是「查账」「读文档」「什么都不用做(幂等生效了)」。
 * 而 payment_callback_rejected 里的三种 reason 共用一个码,是因为它们的下一步是同一件:
 * 有人去核这笔钱。reason 写在 detail 里给那个人看。
 *
 * ── S-07 又加了两个,对象换成对象存储(M-18)─────────────────
 *   storage_unavailable    签发上传凭证或核对对象是否真的传上去时,阿里云 OSS
 *                          不可用 / 超时 / 明确拒绝。**M-18 的「卡死必须可见」
 *                          就落在这一行上**:飞手那边看到的是一句人话错误,
 *                          而「有多少飞手在海边卡住了」只有这张表答得出来。
 *                          处置动作是去查存储(网络、密钥、桶权限)
 *   storage_unknown_error  OSS 回了一个我们没见过的错误码。处置动作完全不同:
 *                          去读 docs/external-constraints/aliyun-oss.md 与官方文档,
 *                          判断这个码该不该重试。所以必须**原样记下那个码**
 *                          (CLAUDE.md 硬约束:未知的外部错误码必须告警)
 *
 * 为什么不能只留一个「存储故障」:存储的 403 不止一种成因(密钥没配对、桶权限、
 * Policy、签名口径、链接过期),处置动作各不相同 —— 合成一个码之后,
 * 值班的人每次都要从头查一遍。detail 里必须原样带存储回的请求号与错误码
 * (边界矩阵 B4;认不出来的码是唯一的线索)。
 *
 * ── S-07 还加了一个业务事件 ───────────────────────────────
 *   order_status_advanced  订单被推进了一步(from → to + 飞手写的备注)。
 *
 * 它不是哨兵,是**状态机的历史**。理由:`order.status` 是一个单列,它没有历史 ——
 * 不落这一行的话,「这一单什么时候被推到哪一步、谁推的、当时说了什么」
 * 在库里完全不存在。而那恰好是运营端驾驶舱(S-10)与监管端(S-11)要看的东西,
 * 也是客户投诉「货是什么时候到的」时唯一的答案来源。
 * 备注是履约过程里唯一的自由文本(「收货人不在,交给了船上值班的」),
 * 丢掉它等于让那句话只存在于飞手的记忆里。
 *
 * ── S-08 加了一个哨兵,对象是退款 ──────────────────────────
 *   refund_request_failed  退款请求没有成功提交给微信(网络不通 / 微信拒绝 /
 *                          商户号余额不足)。**订单已经取消,而钱还在平台账上。**
 *                          客户看到的是「我取消了,钱没退回来」,库里是
 *                          order.status=cancelled + refund.status=pending ——
 *                          两个界面都不会主动说这件事。它的下一步动作是「补一笔
 *                          真实的退款」,与 payment_callback_rejected 的「核一笔钱」
 *                          完全不同,所以不许合成一个码。
 *
 * ── S-10 加了一个业务事件 ─────────────────────────────────
 *   platform_config_updated  平台配置被改过(优先窗口时长与开关 / 空跑费 / 代办费)。
 *
 * L0-05 要求「每次窗口独占都留审计日志」,那句话盖住的是「窗口拦了谁」;
 * 它没有盖住「窗口的参数什么时候被谁改过」。而后一个问题正是国企合作方
 * (普陀交旅/国航远洋)会问的那一个:他手里有一批「明明没人抢却看不到」的单,
 * 我们要么能调出「那三天窗口被临时调成了 120 分钟」并说清是谁在什么时候调的,
 * 要么只能说「现在的配置是 15 分钟」—— 后者答不了他的问题。
 * detail 里记 changes(每个字段的 from → to)与操作人。
 */
export const AUDIT_EVENT_TYPE = [
  'contact_unlocked',
  'priority_window_exclusive',
  'payment_callback_idempotent_hit',
  'payment_callback_rejected',
  'payment_callback_unknown_event',
  'phone_service_unavailable',
  'phone_quota_exhausted',
  'phone_service_unknown_error',
  // ── S-15 又加了两个,把「超时」与「code 已被消费」从上面三类里拆出来 ──
  //
  // 拆的理由是**钱可能花掉了**:抄本 B2 的扣费时点是「成功回调时」,所以
  // 一次 8 秒超时意味着微信侧可能已经受理并扣了费 —— 而服务端一个字节都没看到。
  //
  //   phone_service_timeout  8 秒超时,结果未知。**这一条是那次可能发生的扣费
  //                          唯一的落点** —— 用户随后重试拿到 40029 时,要对着
  //                          它一起看。混进 phone_service_unavailable 的话,
  //                          「服务连不上」这个说法会替一次已发生的扣费存档,
  //                          而那笔钱从此没有任何地方记得
  //
  //   phone_code_invalid     微信 40029:code 无效 / 过期 / **二次消费**。
  //                          它的下一步动作(重新点一次拿新 code)与 unavailable
  //                          (等一会儿再试)完全不同 —— 对 40029 说「等一会儿」
  //                          只会让人再点一次、再扣一次费
  'phone_service_timeout',
  'phone_code_invalid',
  'storage_unavailable',
  'storage_unknown_error',
  'order_status_advanced',
  'platform_config_updated',
  // ── S-08 加的一个哨兵 ──────────────────────────────────
  //
  //   refund_request_failed  退款请求**没有成功提交给微信**(网络不通 / 微信拒绝 /
  //                          商户号余额不足)。这一单已经取消了,而钱还在平台账上。
  //
  // 为什么它必须是一条独立的哨兵而不是一行 warn:那一单在客户眼里是「我取消了,
  // 钱没退回来」,在库里是 order.status=cancelled + refund.status=pending ——
  // 两个界面都不会主动说这件事,日志文件里的一行 warn 没有值班的人会去翻。
  // 「今天有几单取消了但钱没退出去」是运营每天要问一次的问题,而唯一答得出它的
  // 就是这张表。
  //
  // 它的下一步动作(补一笔真实的退款)与 payment_callback_rejected(核一笔钱是不是
  // 伪造的)完全不同,所以不许合成一个码。
  'refund_request_failed',
  // ── S-19 又加了三个,是**退款回调自己那一套哨兵**(M-15)──
  //
  // 在此之前退款回调的「幂等命中 / 拒绝 / 未知事件」复用了支付侧那三个名字。
  // 契约里加这两个字是因为**可见性落在按 eventType 的过滤上**:
  // 一条「支付回调被拒」实际说的是退款回调时,按名字排查的人会被引到错的地方;
  // 而契约枚举里没有的名字,运营端的筛选器里根本选不出来(只能翻全表)。
  // 结构上两条回调完全相同(同一张幂等表、同一套「不许静默」纪律),名字同构、各自成套。
  //
  //   refund_callback_idempotent_hit  退款回调重复推送被幂等挡下(钱一分没重退)
  //   refund_callback_rejected        验签过了但内容对不上的退款回调(退款单查不到 /
  //                                   微信退款单号与记录不符 / 金额与退款单不符 /
  //                                   认不出来的 refund_status)。下一步是去核这笔退款
  //   refund_callback_unknown_event   微信推来没见过的退款通知 event_type。下一步是读文档
  'refund_callback_idempotent_hit',
  'refund_callback_rejected',
  'refund_callback_unknown_event',
  // ── S-12 又加了三个,对象换成订阅消息的额度与下发(Y-04、抄本 B3)──
  //
  //   subscribe_quota_invalid  微信回 43101。本地把该 (openid + 模板) 的剩余次数清零,
  //                            下次用户进相关页面时重新引导订阅。**它不是硬故障,但必须可见**:
  //                            一次性订阅下「额度没了」= 通知静默停发,而客户说
  //                            「我什么都没收到」的那一刻,唯一答得出「什么时候停的」就是这一行。
  //                            43101 一个码三种含义(从未订阅 / 用户取消 / 次数用完),
  //                            按字面理解(用户不想收)**会得出完全错的结论**,所以它不能只是一行 warn
  //
  //   subscribe_send_failed    重试 2 次后仍然没发出去,落库待补。运营照那份待补清单补发 ——
  //                            与上面那条的区别是「额度还在,只是这一次没发出去」
  //
  //   subscribe_unknown_error  微信回了一个我们没见过的 errcode。处置动作完全不同:
  //                            去读官方文档,判断这个码该不该重试。所以必须**原样记下那个码**
  //                            (CLAUDE.md 硬约束:未知的外部错误码必须告警)
  //
  // 后两类不许合成一类:一个的下一步是补发,另一个的下一步是读文档。
  // 「计数为 0 没发」与「人工指派没有飞手可发」不在这里 —— 那两档不需要人处理,
  // 但它们必须留痕(subscribe_send 表里有各自的终态),因为通知停发是静默的。
  'subscribe_quota_invalid',
  'subscribe_send_failed',
  'subscribe_unknown_error',
  // ── S-51 加的一个业务事件 ─────────────────────────────────
  //
  //   takeoff_application_reviewed  监管端审核了一条起飞申请(Y-36 开的唯一一条写路由)。
  //
  // 它不是哨兵,是**业务事件的留痕**。申请行上虽然记着 status / reviewNote /
  // reviewedAt / reviewedByAccountId,但那是「现在长什么样」,没有历史 ——
  // 不落这一行的话,「这条申请什么时候被谁审的、结论是什么、当时的意见」
  // 在库里完全不存在,而那是「这条单被驳过几次、为什么」唯一的答案来源
  // (Y-40:驳回本身是记录)。AS-101 写明了这一条。
  //
  // 它是全系统唯一一条**监管端**写出来的审计事件 —— 与「监管端只读」这条性质
  // 不冲突:审核是唯一被开出来的写动作(Y-36),而它自己必须留痕。
  'takeoff_application_reviewed',
  // ── S-55 加的一个业务事件 ─────────────────────────────────
  //
  //   order_receipt_confirmed_by_ops  运营端替收件人代确认收货(Y-47 的一半)。
  //
  // 它不是哨兵,是**业务事件的留痕**。订单上虽然记着 signedAt / signedVia / signedByUserId,
  // 但那是「现在长什么样」,没有历史 —— 而运营代确认是**替别人做的一个决定**:
  // 收件人没点、也没到自动签收的天数,是运营把这一单的完成闸门推开的。
  // 不落这一行的话,「这一单是谁在什么时候替收件人签的、当时为什么」在库里完全不存在,
  // 而纠纷里最先被问的正是这一句(「我根本没收到,谁给我签的?」)。
  //
  // 收件人自己确认(user)与到点自动签收(auto)不落这一行:
  // 前者是当事人自己的动作(订单上的 signedByUserId 就是那个账号),后者不是任何人的决定。
  // 只有「运营替人签」需要一条独立的记录 —— 它记的是**一个本不该由系统做出的动作**。
  // detail 里记 signedVia 与 note。
  'order_receipt_confirmed_by_ops',
] as const

export type AuditEventType = (typeof AUDIT_EVENT_TYPE)[number]

export function isAuditEventType(x: unknown): x is AuditEventType {
  return typeof x === 'string' && (AUDIT_EVENT_TYPE as readonly string[]).includes(x)
}

/**
 * **手机号接口的哨兵族** —— 上面那五个 phone_* 取值,按枚举里的顺序(S-44 加)。
 *
 * 它的用途只有一个:运营端的审计日志页(v2 · O3)顶部那块「需要有人看的告警」
 * 按这五个取值从审计数据里派生(每类各查一次,合并按时刻倒序)。
 *
 * 为什么它们是「一条流」而不是五条普通事件:额度耗尽、接口不可用、未知错误、
 * 超时、code 已被用过 —— 这五种失败在页面上长得一模一样(都是「登录不动了」),
 * 而**处置动作各不相同**(去充值 / 去修服务 / 去读文档 / 去核一笔可能已扣的费 /
 * 让用户重新点一次拿新 code)。M-17 把「额度耗尽 = 全站登录静默失败」这件事
 * 显式接受为代价,而这几条日志是它唯一的发现路径 —— 混在二十一类事件里翻,
 * 等于没有。
 *
 * `satisfies readonly AuditEventType[]` 是**编译期的双向校验里能动的那一半**:
 * 拼错一个名字、或者哪天有人删掉枚举里某个取值,这里当场 typecheck 报红。
 * 它保证不了的那一半:S-15 那样的「又加了一种手机号失败」不会让这个列表报红 ——
 * 那时要有人记得把新取值加进来(产出点 phone.service.ts 的 MAPPING 是它的上游,
 * 那份 MAPPING 与这个列表的对应关系靠人核)。
 */
export const PHONE_ALERT_EVENT_TYPES = [
  'phone_service_unavailable',
  'phone_quota_exhausted',
  'phone_service_unknown_error',
  'phone_service_timeout',
  'phone_code_invalid',
] as const satisfies readonly AuditEventType[]
